import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { FormError } from '@/components/ui/form-error';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { OccasionItem } from '@/hooks/queries/use-relationship';
import { parseLocalDate } from '@/lib/utils';

const occasionItemSchema = z.object({
  title: z.string().min(1, 'O título é obrigatório'),
  date: z.string().min(1, 'A data é obrigatória'),
});

export type OccasionItemFormValues = z.infer<typeof occasionItemSchema>;

interface OccasionItemFormProps {
  item?: OccasionItem;
  onSubmit: (data: OccasionItemFormValues) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

function formatDateForInput(dateString: string): string {
  const date = parseLocalDate(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function OccasionItemForm({
  item,
  onSubmit,
  onCancel,
  isLoading = false,
}: OccasionItemFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OccasionItemFormValues>({
    resolver: zodResolver(occasionItemSchema),
    defaultValues: {
      title: item?.title ?? '',
      date: item?.date ? formatDateForInput(item.date) : '',
    },
  });

  useEffect(() => {
    if (item) {
      reset({
        title: item.title,
        date: formatDateForInput(item.date),
      });
    } else {
      reset({
        title: '',
        date: '',
      });
    }
  }, [item, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="title">Título *</Label>
        <Input
          id="title"
          placeholder="Ex: Aniversário de namoro"
          disabled={isLoading}
          {...register('title')}
        />
        <FormError message={errors.title?.message} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="date">Data *</Label>
        <Input
          id="date"
          type="date"
          disabled={isLoading}
          {...register('date')}
        />
        <FormError message={errors.date?.message} />
      </div>

      <div className="flex gap-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1"
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading} className="flex-1">
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {item ? 'Salvar' : 'Adicionar'}
        </Button>
      </div>
    </form>
  );
}
